import { TaxIdBase } from './tax-id-base';

export class ColombiaTaxId extends TaxIdBase {
  private readonly NIT_WEIGHTS: number[] = [71, 67, 59, 53, 47, 43, 41, 37, 29, 23, 19, 17, 13, 7, 3];
  private readonly COEFFICIENT: number = 11;
  private readonly ACCEPTED_CHARS_REGEX: RegExp = /^[\d.\s-]+$/;
  private readonly NON_DIGIT_REGEX: RegExp = /\D/g;
  // Base number excluding the verification digit: XX.XXX.XXX-X through X.XXX.XXX.XXX-X.
  private readonly MIN_BASE_LENGTH: number = 8;
  private readonly MAX_BASE_LENGTH: number = 10;
  private readonly MIN_RUT_LENGTH: number = this.MIN_BASE_LENGTH + 1;
  private readonly MAX_RUT_LENGTH: number = this.MAX_BASE_LENGTH + 1;

  public override getFormattedTaxId(value: string): string {
    if (!value) return '';

    const digits: string = this.getDigits(value);
    // Not a RUT at all. Leave it alone rather than partly grouping it.
    if (digits.length < this.MIN_RUT_LENGTH || digits.length > this.MAX_RUT_LENGTH) return value;

    return `${this.groupFromRight(digits.slice(0, -1))}-${digits.slice(-1)}`;
  }

  public override isTaxIdValid(taxId: string): boolean {
    const value: string = (taxId ?? '').trim();
    if (!this.ACCEPTED_CHARS_REGEX.test(value)) return false;

    // Accepts both the formatted display form and the raw digits used for storage.
    const digits: string = this.getDigits(value);
    if (digits.length < this.MIN_RUT_LENGTH || digits.length > this.MAX_RUT_LENGTH) return false;

    const nit: string = digits.substring(0, digits.length - 1);
    const verificationDigit: number = Number(digits.substring(digits.length - 1));

    return this.calcVerificationDigit(nit) === verificationDigit;
  }

  private calcVerificationDigit(nit: string): number {
    const weights: number[] = this.NIT_WEIGHTS.slice(this.NIT_WEIGHTS.length - nit.length);
    const sum: number = nit.split('').reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);

    const modResult: number = sum % this.COEFFICIENT;
    return modResult < 2 ? modResult : this.COEFFICIENT - modResult;
  }

  private groupFromRight(digits: string): string {
    const groups: string[] = [];
    for (let end = digits.length; end > 0; end -= 3) {
      groups.unshift(digits.slice(Math.max(0, end - 3), end));
    }
    return groups.join('.');
  }

  private getDigits(value: string): string {
    return value.replace(this.NON_DIGIT_REGEX, '');
  }
}

export const ColombiaTaxIdHelper = new ColombiaTaxId();
